import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Sunrise, Sunset } from "lucide-react";
import { useInit } from "@follosoft/react-components";
import {
  formatTime,
  isAlertScheduleRestricted,
} from "@/utils/alerts-schedule";

const SETTINGS_GROUP = "custom_phone_number";
const START_TIME_KEY = "schedule_start_time";
const END_TIME_KEY = "schedule_end_time";
const RESTRICT_KEY = "restrict_alert_hours";

/**
 * The hours during which the company places call and text alerts, taken from
 * the init settings. Shown at sign-up/renewal so the customer knows when to
 * expect a call or text before agreeing to the service. Emails are sent
 * regardless of the schedule, so they are deliberately not mentioned.
 */
const AlertsScheduleNotice: FC = () => {
  const { t } = useTranslation();
  const { settingByGroupAndKey, account } = useInit();

  const startSetting = settingByGroupAndKey(SETTINGS_GROUP, START_TIME_KEY);
  const endSetting = settingByGroupAndKey(SETTINGS_GROUP, END_TIME_KEY);
  const restrictSetting = settingByGroupAndKey(SETTINGS_GROUP, RESTRICT_KEY);

  // Without restrict_alert_hours the backend ignores the window, so showing
  // the times would promise a limit that does not exist.
  const restricted = isAlertScheduleRestricted(
    restrictSetting?.attributes?.value ??
      restrictSetting?.attributes?.default_value
  );
  if (!restricted) {
    return null;
  }

  const start = formatTime(
    startSetting?.attributes?.value ?? startSetting?.attributes?.default_value
  );
  const end = formatTime(
    endSetting?.attributes?.value ?? endSetting?.attributes?.default_value
  );

  if (!start || !end) {
    return null;
  }

  // The backend resolves the window in the account's timezone, so show it.
  const timezone = account?.attributes?.timezone;

  const bounds = [
    {
      key: "start",
      icon: Sunrise,
      time: start,
      title: t("fe.pages.flow.steps.alerts.scheduleStartTitle"),
      description: t("fe.pages.flow.steps.alerts.scheduleStartDescription"),
    },
    {
      key: "end",
      icon: Sunset,
      time: end,
      title: t("fe.pages.flow.steps.alerts.scheduleEndTitle"),
      description: t("fe.pages.flow.steps.alerts.scheduleEndDescription"),
    },
  ];

  return (
    <div className="mt-4 w-full rounded-2xl border border-border bg-card p-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {bounds.map(({ key, icon: Icon, time, title, description }) => (
          <div key={key} className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon size={18} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm leading-5 font-medium text-card-foreground">
                {title}
              </span>
              <span className="text-xs leading-4 text-muted-foreground">
                {description}
              </span>
              <span className="mt-1 text-base leading-6 font-semibold tabular-nums text-primary">
                {time}
                {timezone && (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    {timezone}
                  </span>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlertsScheduleNotice;
